import React, { createContext, useContext, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger);

// 1. Create the Context
const AnimationContext = createContext(null);

// 2. Custom Hook to use the Context
export const useAnimations = () => {
    const context = useContext(AnimationContext);
    if (!context) {
        throw new Error('useAnimations must be used within an AnimationProvider');
    }
    return context;
};

// 3. The Provider Component
export const AnimationProvider = ({ children }) => {
    // Use a ref to target the main app container for ScrollTrigger scoping (best practice)
    const mainRef = useRef(null);

    // You can expose functions here to manage complex animations or timelines.

    // This hook ensures GSAP and ScrollTrigger are ready.
    useGSAP(() => {
        // You can set global GSAP defaults here if needed
        // gsap.defaults({ ease: "power3.inOut" });
    }, { scope: mainRef });


    const contextValue = {
        // Expose the main ref for components to attach to if needed
        mainRef,
        // Expose a function to create a simple entrance animation (e.g., for headers)
        createEntranceAnimation: (target, vars = {}) => {
            // Default entrance animation: fade up
            gsap.from(target, {
                opacity: 0,
                y: 50,
                duration: 1,
                ease: "power3.out",
                ...vars, // Allow overriding defaults
            });
        },

        // Expose a function for ScrollTriggered entrance (for sections further down)
        createScrollEntrance: (target, trigger, vars = {}) => {
            gsap.from(target, {
                opacity: 0,
                y: 100,
                duration: 1.2,
                scrollTrigger: {
                    trigger: trigger,
                    start: "top 80%",
                    end: "bottom top",
                    toggleActions: "play none none reverse", // Play once when scrolling down
                    // markers: true, // Uncomment for debugging
                },
                ...vars,
            });
        },
    };

    return (
        // Wrap your entire app with the ref container for GSAP scoping
        <div ref={mainRef} className="gsap-scope-container">
            <AnimationContext.Provider value={contextValue}>
                {children}
            </AnimationContext.Provider>
        </div>
    );
};
